import React from 'react'
import {injectIntl} from 'react-intl'
import styled from 'styled-components'
import FormControlLabel from '@mui/material/FormControlLabel'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import Checkbox from '@mui/material/Checkbox'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import {KeyboardArrowDown} from '@mui/icons-material'
import Autocomplete from 'components/Autocomplete'
import Editor from 'components/Editor'
import TestMessage from 'components/TestMessage'
import SendingError from 'components/SendingError'
import {getEmailSubstitutions, getFormEmailOptions, getTableEmailOptions,
    getFieldsByType, getDateSubstitutions, getDurationSubstitutions} from 'utils/utils'
import {fieldtype} from 'utils/enums/fieldtype'
import periodicity from 'utils/enums/periodicity'

const Row = styled.div`
    display: flex;
    margin-bottom: 20px;
`

const Field = styled(TextField)`
	width: calc(50% - 15px);
	margin-right: 30px;

	&:nth-child(2n) {
		margin-right: 0
	}
`

const Arrow = styled(KeyboardArrowDown)`
	transition: transform .3s cubic-bezier(.55, 0, .1, 1);
`

class EmailTemplate extends React.Component {
    constructor(props) {
        super(props)

        this.substitutions = getEmailSubstitutions(props.entity)
        this.forms = getFormEmailOptions(props.entity)
        this.tables = getTableEmailOptions(props.emission.pathId)
        this.fields = getFieldsByType(props.entity, fieldtype.check)
        this.dates = getDateSubstitutions(props.entity)
        this.numbers = getDurationSubstitutions(props.entity)

        this.state = {
            name: props.template ? props.template.name : '',
            subject: props.template ? props.template.subject : '',
            fromEmail: props.template ? props.template.fromEmail : '',
            fromName: props.template ? props.template.fromName : '',
            cc: props.template ? props.template.cc.join(', ') : '',
            bcc: props.template ? props.template.bcc.join(', ') : '',
            body: props.template ? props.template.body : '',
            gdprOnly: props.template ? props.template.gdprOnly : false,
            feedbackPeriodicity: props.template ? props.template.feedbackPeriodicity : periodicity.Month.value,
			anchorEl: null
        }
    }

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        })
    }

    handleCreate = () => {
        this.props.createEmailTemplate({
            entityId: this.props.entity,
            emissionPath: this.props.emission.pathId,
            name: this.state.name,
            subject: this.state.subject,
            fromEmail: this.state.fromEmail,
            fromName: this.state.fromName,
            cc: this.state.cc ? this.state.cc.split(',').map(item => item.trim()) : [],
            bcc: this.state.bcc ? this.state.bcc.split(',').map(item => item.trim()) : [],
            body: this.state.body,
            gdprOnly: this.state.gdprOnly,
            feedbackPeriodicity: this.state.feedbackPeriodicity,
            type: 0
        })
        this.props.handleClose()
    }

    handleUpdate = () => {
        this.props.updateEmailTemplate({
            id: this.props.template.id,
            entityId: this.props.entity,
            emissionPath: this.props.emission.pathId,
            name: this.state.name,
            subject: this.state.subject,
            fromEmail: this.state.fromEmail,
            fromName: this.state.fromName,
            cc: this.state.cc ? this.state.cc.split(',').map(item => item.trim()) : [],
            bcc: this.state.bcc ? this.state.bcc.split(',').map(item => item.trim()) : [],
            body: this.state.body,
            gdprOnly: this.state.gdprOnly,
            feedbackPeriodicity: this.state.feedbackPeriodicity,
            type: 0
        })
        this.props.handleClose()
    }

    handleDelete = () => {
        this.props.deleteEmailTemplate(this.props.template.id)
        this.props.handleClose()
    }

	handleClone = () => {
		this.props.cloneEmailTemplate(this.props.template.id)
        this.props.handleClose()
	}

    handleSend = (save, address = '') => {
        const template = {
            entityId: this.props.entity,
            emissionPath: this.props.emission.pathId,
            name: this.state.name,
            subject: this.state.subject,
            fromEmail: this.state.fromEmail,
            fromName: this.state.fromName,
            cc: this.state.cc ? this.state.cc.split(',').map(item => item.trim()) : [],
            bcc: this.state.bcc ? this.state.bcc.split(',').map(item => item.trim()) : [],
            body: this.state.body,
            gdprOnly: this.state.gdprOnly,
            feedbackPeriodicity: this.state.feedbackPeriodicity,
            type: 0
        }

        if (this.props.template) {
            this.props.sendEmailTemplate({
                id: this.props.template.id,
                ...template
            }, save, address)
        } else {
            this.props.sendEmailTemplate({
                ...template
            }, save, address)
        }

		this.onClose()
    }

    handleSendTest = (address) => {
        this.handleSend(false, address)
    }

    handleClose = () => {
        if (!this.props.test) {
            this.props.handleClose()
        }
        
        this.props.handleReset()
    }

	handleClick = (e) => {
		if (!this.props.sending) {
			if (this.props.template) {
				this.handleUpdate()
			} else {
				this.handleCreate()
			}
		} else {
			this.setState({anchorEl: e.currentTarget})
		}
	}

	onClose = () => {
		this.setState({anchorEl: null})
	}

	handleConfirm = (e) => {
		if (this.props.template) {
			this.handleUpdate()
		} else {
			this.handleCreate()
		}

		this.onClose()
	}

    render() {
        const {intl} = this.props
        const validation = !this.state.name || !this.state.subject ||
            !this.state.fromName || !this.state.fromEmail

        return (
            <React.Fragment>
                <DialogTitle>
                    {this.props.template ?
                        intl.formatMessage({id: "sidebar.send_to"}, {
                            name: this.props.template.name, to: this.props.emission.pathName})
                        :
                        intl.formatMessage({id: "sidebar.send_to"}, {
                            name: '', to: this.props.emission.pathName})
                    }
                </DialogTitle>
                <DialogContent sx={{overflowY: 'visible'}}>
                    <Row>
                        <Field variant="standard"
                            value={this.state.name}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.template_name"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                maxLength: global.NAME_MAX_LENGTH,
                                name: 'name'
                            }}
                        />
                        <Autocomplete
                            style={{height: '100%', width: 'calc(50% - 15px)'}}
                            value={this.state.subject}
                            label={intl.formatMessage({id: "sidebar.subject"})}
                            onChange={(value) => this.setState({subject: value})}
                            options={this.substitutions.map(field => field.name)}
                            component='input'
                        />
                    </Row>
                    <Row>
                        <Field variant="standard"
                            value={this.state.fromEmail}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.from_email"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                maxLength: global.NAME_MAX_LENGTH,
                                name: 'fromEmail'
                            }}
                        />
                        <Field variant="standard"
                            value={this.state.fromName}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.from_name"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                maxLength: global.NAME_MAX_LENGTH,
                                name: 'fromName'
                            }}
                        />
                    </Row>
                    <Row>
                        <Field variant="standard"
                            value={this.state.cc}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.carbon_copy"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                name: 'cc'
                            }}
                        />
                        <Field variant="standard"
                            value={this.state.bcc}
                            onChange={this.handleChange}
                            label={intl.formatMessage({id: "sidebar.blind_carbon_copy"})}
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                name: 'bcc'
                            }}
                        />
                    </Row>
                    <Editor
                        subs={this.substitutions}
                        forms={this.forms}
                        tables={this.tables}
                        fields={this.fields}
                        value={this.state.body}
                        onChange={(value) => this.setState({body: value})}
                        urls={this.substitutions.filter(item => item.type === 'url')}
                        dates={this.dates}
                        numbers={this.numbers}
                    />
                    <Row>
                        <FormControl sx={{width: 'calc(50% - 15px)'}} variant="standard">
                            <InputLabel>
                                {intl.formatMessage({id: "editor.feedback_periodicity"})}
                            </InputLabel>
                            <Select
                                value={this.state.feedbackPeriodicity}
                                onChange={this.handleChange}
                                inputProps={{
                                    name: 'feedbackPeriodicity'
                                }}
                            >
                                {Object.keys(periodicity).map(item =>
                                    <MenuItem key={periodicity[item].value} value={periodicity[item].value}>
                                        {intl.formatMessage({id: `editor.periodicity_${periodicity[item].label}`})}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Row>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={this.state.gdprOnly}
                                onChange={(e) => this.setState({ gdprOnly: e.target.checked })}
                                inputProps={{
                                    name: 'gdprOnly'
                                }}
                            />
                        }
                        label={intl.formatMessage({id: "sidebar.gdpr_only"})}
                    />
                    {this.props.sending &&
                        <TestMessage
                            type='email'
                            handleSend={this.handleSendTest}
                            title={this.props.template ?
                                {name: this.props.template.name, to: this.props.emission.pathName} :
                                {name: '', to: this.props.emission.pathName}
                            }
                        />
                    }
                    {this.props.errors &&
                        <SendingError
                            successes={this.props.successes}
                            errors={this.props.errors}
                            handleClose={this.handleClose}
                            // handleReset={this.props.handleReset}
                            title={this.props.template ?
                                {name: this.props.template.name, to: this.props.emission.pathName} :
                                {name: '', to: this.props.emission.pathName}
                            }
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.handleClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    {this.props.template &&
                        <Button variant="contained"
                            onClick={this.handleDelete}
                            color="secondary"
                        >
                            {intl.formatMessage({id: "app.confirm_delete"})}
                        </Button>
                    }
					{this.props.template &&
                        <Button variant="contained"
                            onClick={this.handleClone}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.duplicate"})}
                        </Button>
                    }
					<Button variant="contained"
						disabled={validation}
						onClick={this.handleClick}
						color="primary"
						endIcon={this.props.sending ?
							<Arrow style={this.state.anchorEl ? {transform: 'rotate(-180deg)'} : {}} /> : null}
					>
						{this.props.template ?
							intl.formatMessage({id: "app.confirm_update"}) :
							intl.formatMessage({id: "app.confirm_save"})}
					</Button>
					{this.state.anchorEl &&
						<Menu
							anchorEl={this.state.anchorEl}
							open={!!this.state.anchorEl}
							onClose={this.onClose}
							anchorOrigin={{
								vertical: 'top',
								horizontal: 'center',
							}}
							transformOrigin={{
								vertical: 'bottom',
								horizontal: 'center',
							}}
						>
							<MenuItem onClick={this.handleConfirm}>
								{this.props.template ?
									intl.formatMessage({id: "app.confirm_update"}) :
									intl.formatMessage({id: "app.confirm_save"})}
							</MenuItem>
							<MenuItem onClick={() => this.handleSend(false)}>
								{intl.formatMessage({id: "app.confirm_send"})}
							</MenuItem>
							<MenuItem onClick={() => this.handleSend(true)}>
								{this.props.template ?
									intl.formatMessage({id: "app.confirm_update_send"}) :
									intl.formatMessage({id: "app.confirm_save_send"})}
							</MenuItem>
						</Menu>
					}
                </DialogActions>
            </React.Fragment>
        )
    }
}

export default injectIntl(EmailTemplate)
